/**
 * CABViewer — Full-screen viewer for the Common Application Blueprint HTML preview.
 * Opened via "Open in New Tab" from the CAB mode preview panel.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCABHtml } from '../services/cabApi';

const CABViewer: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCABPreview = async () => {
      if (!projectId) {
        setError('Project ID is missing');
        setLoading(false);
        return;
      }

      try {
        const html = await getCABHtml(projectId);
        if (html) {
          setHtmlContent(html);
        } else {
          setError('No CAB preview available');
        }
      } catch (err: any) {
        console.error('Error fetching CAB preview:', err);
        setError(err.response?.data?.detail || 'Failed to load CAB preview');
      } finally {
        setLoading(false);
      }
    };

    fetchCABPreview();
  }, [projectId]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bg-brand-solid mx-auto" />
          <p className="mt-bg-v2-md text-text-secondary">Loading CAB preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <p className="text-text-error-primary mb-bg-v2-md">{error}</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-sm bg-bg-brand-solid text-text-white rounded-bg-v2-md hover:bg-bg-brand-solid-hover transition-colors"
          >
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  if (!htmlContent) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <p className="text-text-secondary mb-bg-v2-md">No CAB preview available</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-sm bg-bg-brand-solid text-text-white rounded-bg-v2-md hover:bg-bg-brand-solid-hover transition-colors"
          >
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-bg-primary">
      <iframe
        srcDoc={htmlContent}
        className="w-full h-full border-0"
        title="CAB Preview"
        sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
      />
    </div>
  );
};

export default CABViewer;
